import { useSelect } from 'downshift';
import { ReactComponent as ArrowDownIcon } from '../../../../assets/icon/arrow-down.svg';
import { ReactComponent as CheckedIcon } from '../../../../assets/icon/checked.svg';
import { useTranslation } from 'react-i18next';
import SimpleBar from 'simplebar-react';
import { LegacyRef, useMemo, useState } from 'react';
import { usePopper } from 'react-popper';
import { isNil } from 'lodash';
import { ModifierArguments, ModifierPhases } from '@popperjs/core';
import { cx, css, CSSInterpolation, CSSObject } from 'core/emotion';
import Portal from 'baseUI/Portal';
import { useTheme } from 'theme';

export type Option<T> = {
  label: string;
  value: T;
};

export type Action = {
  label: string;
  onClick?: () => void;
};

export type ValueEqual<T> = (value: T | null) => string | null;

const dropdownMenuShadowStyle = css`
  box-shadow: 0px 7px 15px rgba(27, 80, 113, 0.1);
`;

export type DropdownStyled = {
  root?: CSSInterpolation;
  button?: {
    root?: CSSInterpolation;
    icon?: CSSInterpolation;
    placeholder?: string;
  };
  scrollbar?: {
    navigatorColor?: string;
    backgroundColor?: string;
  };
  option?: CSSInterpolation;
  selectedOption?: CSSInterpolation;
  iconCheckedColor?: string;
};

type DropdownProps<T> = {
  className?: string;
  placeholder?: string;
  options: Option<T>[];
  onChange: (value: T | null) => void;
  value: T | null;
  disabled?: boolean;
  menuClassName?: string;
  valueEqual?: ValueEqual<T>;
  simpleBarClassName?: string;
  rootDropdownPortal?: HTMLDivElement;
  dropdownStyled?: DropdownStyled;
};

const itemToString = <T,>(item: Option<T> | null) => item?.label ?? '';

const Dropdown = <T,>({
  className,
  placeholder,
  options,
  onChange,
  value,
  disabled,
  menuClassName,
  valueEqual = (value) => (isNil(value) ? null : `${value}`),
  simpleBarClassName,
  rootDropdownPortal,
  dropdownStyled,
}: DropdownProps<T>) => {
  const {
    isOpen,
    selectedItem,
    getToggleButtonProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
  } = useSelect({
    items: options,
    itemToString,
    onSelectedItemChange: ({ selectedItem }) => {
      onChange(selectedItem?.value ?? null);
    },
    selectedItem:
      options.find((item) => valueEqual(item.value) === valueEqual(value)) ??
      null,
  });

  const { t } = useTranslation();

  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);

  const widthModifier = useMemo(() => {
    return {
      name: 'sameWidth',
      enabled: true,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      fn: ({ state }: ModifierArguments<any>) => {
        state.styles.popper.width = `${state.rects.reference.width}px`;
      },
      phase: 'beforeWrite' as ModifierPhases,
      requires: ['computeStyles'],
    };
  }, []);
  const theme = useTheme();
  const hoverBorderColor = useMemo(() => {
    return theme.getGlobalTheme().getDark600Color();
  }, [theme]);

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'bottom-start',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 4],
        },
      },
      {
        name: 'flip',
        enabled: true,
        options: {
          padding: {
            bottom: 30,
          },
        },
      },
      widthModifier,
    ],
  });

  const scrollbarStyle = useMemo(
    () =>
      cx(
        css`
          max-height: 200px;
          .simplebar-track {
            width: 5px;
          }

          .simplebar-track.simplebar-vertical {
            margin: 0px 1px 0px 8px;
          }

          .simplebar-track.simplebar-vertical .simplebar-scrollbar:before {
            top: 0px;
            bottom: 0px;
            width: 5px;
            left: 0;
            opacity: 1;
            background: #e1e1e1;
            border-radius: 25px;
          }
        `,
        css({
          '&& .simplebar-track.simplebar-vertical .simplebar-scrollbar:before':
            {
              backgroundColor: dropdownStyled?.scrollbar?.navigatorColor,
            },
          '&& .simplebar-track': {
            backgroundColor: dropdownStyled?.scrollbar?.backgroundColor,
          },
        })
      ),
    [dropdownStyled]
  );

  const dropdownMenuBorderColor = useMemo(() => {
    return theme.getGlobalTheme().getDark50Color();
  }, [theme]);

  return (
    <div
      className="relative"
      ref={setReferenceElement as LegacyRef<HTMLDivElement>}
    >
      <div className={cx('flex flex-col gap-1', className)}>
        <button
          disabled={disabled}
          aria-label="toggle menu"
          className={cx(
            'rounded-medium border-1 relative flex h-9 w-full items-center justify-between bg-white p-3 text-sm focus:ring-0',
            disabled ? 'cursor-not-allowed opacity-50' : '',
            menuClassName,
            selectedItem
              ? 'text-blue-dark-900 border-primary'
              : 'text-gray-410 border-gray-210',
            css({
              '&&': dropdownStyled?.button?.root,
              ':hover': {
                borderColor: hoverBorderColor,
              },
            })
          )}
          type="button"
          {...getToggleButtonProps()}
        >
          <span
            className={cx(
              'flex-nowrap truncate',
              css`
                max-width: 95%;
              `
            )}
          >
            {selectedItem ? t(itemToString(selectedItem)) : placeholder}
          </span>
          <span className="inline-block pl-1">
            <ArrowDownIcon
              className={cx(
                'text-blue-dark-900 transform',
                isOpen ? 'rotate-180' : '',
                disabled ? 'cursor-not-allowed opacity-50' : 'opacity-100',
                css({
                  '&&': {
                    color: (dropdownStyled?.button?.icon as CSSObject)?.color,
                  },
                })
              )}
            />
          </span>
        </button>
      </div>
      <div>
        {isOpen ? (
          <Portal enable={true} root={rootDropdownPortal}>
            <div
              {...getMenuProps(
                {
                  ref: setPopperElement as LegacyRef<HTMLDivElement>,
                },
                { suppressRefError: true }
              )}
              className={cx(
                'rounded-medium border-1 absolute z-10 max-h-80 overflow-auto bg-white',
                dropdownMenuShadowStyle
              )}
              style={{ ...styles.popper, borderColor: dropdownMenuBorderColor }}
              {...attributes.popper}
            >
              <SimpleBar
                forceVisible={false}
                autoHide={true}
                id="code-snippet"
                className={cx(scrollbarStyle, simpleBarClassName)}
              >
                {options.map((item, index) => (
                  <div
                    className={cx(
                      highlightedIndex === index && 'bg-gray-50',
                      selectedItem === item
                        ? cx(
                            'bg-blue-light-50',
                            css({ '&&': dropdownStyled?.selectedOption })
                          )
                        : css({ '&&': dropdownStyled?.option }),
                      'py-10px text-neutral-gray flex cursor-pointer items-center pl-4 pr-3 text-sm'
                    )}
                    {...getItemProps({ item, index })}
                  >
                    <span className="flex-nowrap truncate">
                      {t(item.label)}
                    </span>
                    {selectedItem === item ? (
                      <CheckedIcon
                        className={cx(
                          'text-blue-light-500 ml-auto',
                          css({
                            '&&': {
                              color: dropdownStyled?.iconCheckedColor,
                            },
                          })
                        )}
                      />
                    ) : null}
                  </div>
                ))}
              </SimpleBar>
            </div>
          </Portal>
        ) : null}
      </div>
    </div>
  );
};

export default Dropdown;
